type EventEndScheduleParams = {
	eventId: string | null | undefined;
	endDate: Date | null | undefined;
};

/** `setTimeout` max delay in browsers (~24.8 days). */
const MAX_SCHEDULE_MS = 2_147_483_647;

let endTimer: ReturnType<typeof setTimeout> | null = null;
let scheduleKey: string | null = null;
let onCalendarEnd: (() => void) | null = null;

/** Register once from the event store — runs admin-calendar teardown when `end_date` elapses. */
export function onEventEnd(handler: () => void): void {
	onCalendarEnd = handler;
}

export function clearEventEndSchedule(): void {
	if (endTimer !== null) {
		clearTimeout(endTimer);
		endTimer = null;
	}
	scheduleKey = null;
}

/**
 * Schedule a one-shot timeout for admin `end_date`. Re-schedules when event id or end instant changes.
 */
export function scheduleEventEnd(params: EventEndScheduleParams): void {
	if (!onCalendarEnd) return;

	const { eventId, endDate } = params;
	if (!eventId || !(endDate instanceof Date)) {
		clearEventEndSchedule();
		return;
	}

	const endMs = endDate.getTime();
	if (!Number.isFinite(endMs)) {
		clearEventEndSchedule();
		return;
	}

	const key = `${eventId}|${endMs}`;
	if (scheduleKey === key && endTimer !== null) return;

	clearEventEndSchedule();

	const msUntilEnd = endMs - Date.now();
	if (msUntilEnd <= 0) {
		onCalendarEnd();
		return;
	}

	scheduleKey = key;
	const delayMs = Math.min(msUntilEnd, MAX_SCHEDULE_MS);
	endTimer = setTimeout((): void => {
		endTimer = null;
		scheduleKey = null;
		if (delayMs < msUntilEnd) {
			scheduleEventEnd(params);
			return;
		}
		onCalendarEnd?.();
	}, delayMs);
}

/** Idempotent catch-up when tab slept, clock jumped, or app resumed from background. */
export function catchUpEventEnd(): void {
	onCalendarEnd?.();
}
